/**
 * AI Generation Resilience System
 *
 * This service provides multiple layers of protection against AI generation failures:
 * 1. Real-time monitoring in MeetingDetail (3-second initial check)
 * 2. Persistent tracking in IndexedDB to catch failures across sessions
 * 3. Background retry service for stuck meetings
 * 4. Comprehensive logging and error recovery
 *
 * Key improvements over basic monitor:
 * - Tracks failed attempts in IndexedDB to prevent infinite retries
 * - Implements exponential backoff with jitter to avoid thundering herd
 * - Detects edge function timeout/network errors separately
 * - Logs all attempts for debugging production issues
 * - Validates edge function responses for AI content quality
 */

import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";
import { ensureAuthValid, isAuthError } from "./auth-utils";
import { classifyAIError, isQuotaBlocked, recordQuotaError, getQuotaBlock } from "./ai-quota-guard";
import { isTranscriptLongEnoughForAI } from "./ai-generation-monitor";

export interface AIGenerationAttempt {
  meetingId: string;
  timestamp: number;
  attempt: number;
  success: boolean;
  error?: string;
  missingFields?: string[];
  responseTime?: number;
}

export interface AIGenerationTracker {
  meetingId: string;
  lastAttempt?: AIGenerationAttempt;
  totalAttempts: number;
  lastSuccessTime?: number;
  isStuck: boolean;
  errorPattern?: string;
}

const DB_NAME = 'meetlytic-ai-resilience';
const STORE_NAME = 'generation-attempts';
const TRACKER_STORE = 'generation-trackers';

/** Consecutive failures before a meeting is paused as "stuck". */
const STUCK_FAILURE_THRESHOLD = 3;
/** How long a failure burst suppresses further attempts before it is forgiven. */
const STUCK_COOLDOWN_MS = 5 * 60 * 1000;

/**
 * Initialize IndexedDB for persistent tracking
 */
async function initializeDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Store for individual generation attempts
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('meetingId', 'meetingId', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Store for tracker data (one per meeting)
      if (!db.objectStoreNames.contains(TRACKER_STORE)) {
        db.createObjectStore(TRACKER_STORE, { keyPath: 'meetingId' });
      }
    };
  });
}

/**
 * Log an AI generation attempt
 */
export async function logAIGenerationAttempt(attempt: AIGenerationAttempt): Promise<void> {
  try {
    const db = await initializeDB();
    const transaction = db.transaction([STORE_NAME, TRACKER_STORE], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const trackerStore = transaction.objectStore(TRACKER_STORE);

    // Store the attempt
    store.add(attempt);

    // Update tracker
    const trackerRequest = trackerStore.get(attempt.meetingId);
    trackerRequest.onsuccess = () => {
      const tracker: AIGenerationTracker = trackerRequest.result || {
        meetingId: attempt.meetingId,
        totalAttempts: 0,
        isStuck: false,
      };

      tracker.lastAttempt = attempt;

      if (attempt.success) {
        // The budget in shouldRetryGeneration is meant to stop a meeting that keeps
        // failing, not to cap how many times a healthy meeting may ever be touched.
        // Without this reset the counter only grows, and the tracker persists in
        // IndexedDB — so a meeting crosses the limit and is locked out of automatic
        // retries forever, including the runs that actually succeeded.
        tracker.totalAttempts = 0;
        tracker.lastSuccessTime = attempt.timestamp;
        tracker.isStuck = false;
      } else {
        tracker.totalAttempts += 1;
        // Mark as stuck after a burst of consecutive failures. shouldRetryGeneration
        // ages this out after STUCK_COOLDOWN_MS, so the flag pauses a meeting that is
        // failing right now rather than condemning it — the previous expression
        // (`totalAttempts > 0 ? 1 : 0` compared against 3) could never be true.
        tracker.isStuck = tracker.totalAttempts >= STUCK_FAILURE_THRESHOLD;
        tracker.errorPattern = attempt.error?.substring(0, 100);
      }

      trackerStore.put(tracker);
    };

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    logger.error('[AI-Resilience] Failed to log attempt:', error);
    // Don't throw - this is non-critical logging
  }
}

/**
 * Get generation attempt history for a meeting
 */
export async function getGenerationHistory(meetingId: string): Promise<AIGenerationAttempt[]> {
  try {
    const db = await initializeDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('meetingId');

    return new Promise((resolve, reject) => {
      const request = index.getAll(meetingId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    logger.error('[AI-Resilience] Failed to get history:', error);
    return [];
  }
}

/**
 * Get tracker for a meeting
 */
export async function getGenerationTracker(meetingId: string): Promise<AIGenerationTracker | null> {
  try {
    const db = await initializeDB();
    const transaction = db.transaction([TRACKER_STORE], 'readonly');
    const store = transaction.objectStore(TRACKER_STORE);

    return new Promise((resolve, reject) => {
      const request = store.get(meetingId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    logger.error('[AI-Resilience] Failed to get tracker:', error);
    return null;
  }
}

/**
 * Clear a meeting's failure burst so it starts from a clean slate
 */
async function clearFailureBurst(meetingId: string): Promise<void> {
  try {
    const db = await initializeDB();
    const transaction = db.transaction([TRACKER_STORE], 'readwrite');
    const store = transaction.objectStore(TRACKER_STORE);

    const request = store.get(meetingId);
    request.onsuccess = () => {
      const tracker: AIGenerationTracker | undefined = request.result;
      if (!tracker) return;
      tracker.totalAttempts = 0;
      tracker.isStuck = false;
      store.put(tracker);
    };

    return new Promise((resolve) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve(); // non-critical
    });
  } catch (error) {
    logger.error('[AI-Resilience] Failed to clear failure burst:', error);
  }
}

/**
 * Should we attempt retry? (Checks stuck status and attempt limits)
 */
export async function shouldRetryGeneration(meetingId: string): Promise<boolean> {
  const tracker = await getGenerationTracker(meetingId);

  if (!tracker) {
    return true; // First attempt
  }

  const isBlocked = tracker.isStuck || tracker.totalAttempts >= 5;
  if (!isBlocked) {
    return true;
  }

  // A failure burst describes the system at the moment it happened — a quota outage,
  // a deploy, a dropped connection. The tracker lives in IndexedDB and survives
  // reloads, so without an expiry every meeting opened during a bad five minutes is
  // locked out of automatic recovery permanently. Forgive the burst once it is cold.
  const sinceLastAttempt = Date.now() - (tracker.lastAttempt?.timestamp ?? 0);
  if (sinceLastAttempt >= STUCK_COOLDOWN_MS) {
    logger.log(`[AI-Resilience] Failure burst for ${meetingId} is stale, allowing a fresh attempt`);
    await clearFailureBurst(meetingId);
    return true;
  }

  if (tracker.isStuck) {
    logger.warn(`[AI-Resilience] Meeting ${meetingId} marked as stuck, skipping retry`);
  } else {
    logger.warn(`[AI-Resilience] Meeting ${meetingId} exhausted retry limit (${tracker.totalAttempts})`);
  }
  return false;
}

/**
 * Calculate exponential backoff with jitter
 */
export function calculateBackoffDelay(attempt: number): number {
  const exponentialDelay = Math.pow(2, attempt + 1) * 1000; // 2s, 4s, 8s, 16s
  const jitter = Math.random() * 1000; // 0-1s random jitter
  return exponentialDelay + jitter;
}

/**
 * Validate edge function response contains meaningful AI content
 */
export function validateAIResponse(data: any): { valid: boolean; missingFields: string[] } {
  const missingFields: string[] = [];

  // generate-all-insights short-circuits with { skipped: true } when the meeting is
  // already populated, or when another caller holds the generation lock. Those bodies
  // carry no content to validate — the DB row is the source of truth — so treating
  // them as "incomplete" would burn the retry budget on work that already happened.
  if (data?.skipped) {
    return { valid: true, missingFields: [] };
  }

  const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

  // Check summary
  if (text(data?.summary).length < 50) {
    missingFields.push('summary');
  }

  // Check action items
  if (text(data?.action_items).length < 20) {
    missingFields.push('action_items');
  }

  // Check notes — the edge function returns this as `enhanced_notes` (it lands in the
  // meetings.notes column). Reading only `notes` flagged every successful run as
  // incomplete, which suppressed the refetch and triggered pointless retries.
  if (text(data?.enhanced_notes ?? data?.notes).length < 50) {
    missingFields.push('notes');
  }

  return {
    valid: missingFields.length === 0,
    missingFields,
  };
}

/**
 * Invoke edge function with comprehensive error handling
 */
export async function invokeAIGeneration(
  meetingId: string,
  transcription: string,
  template?: string,
  attempt: number = 1
): Promise<{ success: boolean; data?: any; error?: string; missingFields?: string[]; isAuthError?: boolean; isNonRetryable?: boolean; skipped?: boolean }> {
  const startTime = Date.now();

  // Spend nothing while the account-wide quota is known to be exhausted.
  const quotaBlock = getQuotaBlock();
  if (quotaBlock) {
    logger.debug(
      `[AI-Resilience] Skipping generation for ${meetingId} - Anthropic quota exhausted until ${new Date(quotaBlock.until).toISOString()}`,
    );
    return { success: false, error: 'AI generation paused: Anthropic quota exhausted', isNonRetryable: true };
  }

  try {
    // Ensure we have valid authentication with automatic refresh
    const isAuthValid = await ensureAuthValid();
    if (!isAuthValid) {
      return {
        success: false,
        error: 'Authentication expired. Please refresh and try again.',
        isAuthError: true,
      };
    }

    // Get the session after ensuring it's valid (with retry)
    let accessToken: string | null = null;

    // Try 1: Get from current session.
    // getSession() resolves to { data: { session }, error } — reading access_token off
    // `data` itself always yielded undefined, so this step never succeeded and every
    // generation paid for a needless refreshSession() round-trip below.
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    const session = sessionData?.session;
    if (session?.access_token) {
      accessToken = session.access_token;
    } else if (sessionError) {
      logger.debug('[AI-Resilience] Error getting session:', sessionError.message);
    }

    // Try 2: If session failed, try refreshing and get token from the result
    if (!accessToken) {
      logger.debug('[AI-Resilience] Session token not available, attempting refresh...');
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshData?.session?.access_token) {
        accessToken = refreshData.session.access_token;
        logger.log('[AI-Resilience] Token obtained from refresh');
      } else {
        logger.debug('[AI-Resilience] Refresh failed:', refreshError?.message);
      }
    }

    // Try 3: Last resort - get token from auth state listener (setTimeout to let state propagate)
    if (!accessToken) {
      await new Promise(resolve => setTimeout(resolve, 100));
      const { data: { session: lastSession } } = await supabase.auth.getSession();
      if (lastSession?.access_token) {
        accessToken = lastSession.access_token;
        logger.log('[AI-Resilience] Token obtained after state propagation');
      }
    }

    if (!accessToken) {
      const errorMsg = 'Unable to retrieve authentication token after multiple attempts';
      logger.error('[AI-Resilience]', errorMsg);
      return {
        success: false,
        error: errorMsg,
        isAuthError: true,
      };
    }

    logger.log(`[AI-Resilience] Invoking edge function for meeting ${meetingId} (attempt ${attempt})`);

    const { data, error } = await supabase.functions.invoke('generate-all-insights', {
      headers: { Authorization: `Bearer ${accessToken}` },
      body: {
        transcription,
        template,
        meetingId,
        generateTitle: true,
        // Never force from the automatic recovery path. force=true bypasses the
        // server's "already has content" short-circuit, so a meeting the DB trigger
        // already filled would be regenerated at full cost — and this runs on a 30s
        // scan. Only an explicit user-initiated regenerate should force.
        force: false,
      },
    });

    const responseTime = Date.now() - startTime;

    // Network/transport error
    if (error) {
      const errorMsg = (error as any)?.message || 'Edge function error';
      const authErrorDetected = isAuthError(error);
      logger.error(`[AI-Resilience] Edge function error (${responseTime}ms):`, errorMsg);

      // supabase-js collapses a non-2xx into a generic transport error, so the quota
      // detail can arrive here rather than in a structured response body.
      const kind = classifyAIError(`${errorMsg} ${(error as any)?.context?.details || ''}`);
      if (kind === 'quota') {
        recordQuotaError(`${errorMsg} ${(error as any)?.context?.details || ''}`);
      }

      await logAIGenerationAttempt({
        meetingId,
        timestamp: Date.now(),
        attempt,
        success: false,
        error: errorMsg,
        responseTime,
      });

      // 422 means the server judged this input unprocessable (e.g. transcript too
      // short) — the same request will be rejected the same way every time.
      const isUnprocessable = (error as any)?.context?.status === 422;

      return {
        success: false,
        error: errorMsg,
        isAuthError: authErrorDetected,
        isNonRetryable: kind === 'quota' || kind === 'invalid' || isUnprocessable,
      };
    }

    // Edge function returned error response
    if (!data?.success) {
      const errorMsg = data?.error || data?.details || 'Unknown error';
      logger.error(`[AI-Resilience] Edge function failed (${responseTime}ms): ${errorMsg}`, data);

      // A quota failure is account-wide: trip the breaker so no other meeting,
      // layer, or page reload spends another call rediscovering it.
      const kind = classifyAIError(`${errorMsg} ${data?.details || ''}`);
      if (kind === 'quota') {
        recordQuotaError(`${errorMsg} ${data?.details || ''}`);
      }

      await logAIGenerationAttempt({
        meetingId,
        timestamp: Date.now(),
        attempt,
        success: false,
        error: errorMsg,
        responseTime,
      });

      return { success: false, error: errorMsg, isNonRetryable: kind !== 'retryable' };
    }

    // Validate response contains actual AI content
    const validation = validateAIResponse(data);
    if (!validation.valid) {
      logger.warn(`[AI-Resilience] Edge function returned incomplete content (${responseTime}ms):`, validation.missingFields);

      await logAIGenerationAttempt({
        meetingId,
        timestamp: Date.now(),
        attempt,
        success: false,
        error: 'Incomplete AI content',
        missingFields: validation.missingFields,
        responseTime,
      });

      return {
        success: false,
        error: `Missing fields: ${validation.missingFields.join(', ')}`,
        missingFields: validation.missingFields,
      };
    }

    // A `skipped` body means the server declined the work (content already present,
    // or another caller holds the generation lock) — no Claude call happened and no
    // new content landed. Callers must not report that as a recovery.
    const skipped = data?.skipped === true;

    if (skipped) {
      logger.debug(`[AI-Resilience] Generation skipped by server for ${meetingId} (${responseTime}ms): ${data?.message || 'no reason given'}`);
      return { success: true, data, skipped: true };
    }

    // Success!
    logger.log(`[AI-Resilience] Successfully generated AI content (${responseTime}ms)`);

    await logAIGenerationAttempt({
      meetingId,
      timestamp: Date.now(),
      attempt,
      success: true,
      responseTime,
    });

    return { success: true, data, skipped: false };

  } catch (err) {
    const responseTime = Date.now() - startTime;
    const errorMsg = String(err);
    logger.error(`[AI-Resilience] Exception (${responseTime}ms):`, err);

    await logAIGenerationAttempt({
      meetingId,
      timestamp: Date.now(),
      attempt,
      success: false,
      error: errorMsg,
      responseTime,
    });

    return { success: false, error: errorMsg };
  }
}

/**
 * Retry AI generation with all safeguards
 */
export async function retryWithResilience(
  meetingId: string,
  transcription: string,
  template?: string,
  maxRetries: number = 3
): Promise<{ success: boolean; error?: string; isAuthError?: boolean; skipped?: boolean }> {
  // The edge function rejects short transcripts with a 422; don't spend a call on it
  if (!isTranscriptLongEnoughForAI(transcription)) {
    return { success: false, error: 'Transcript is too short for AI analysis' };
  }

  // Check if we should retry at all
  const shouldRetry = await shouldRetryGeneration(meetingId);
  if (!shouldRetry) {
    return { success: false, error: 'Meeting marked as stuck or retry limit exceeded' };
  }

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    const result = await invokeAIGeneration(meetingId, transcription, template, attempt);

    if (result.success) {
      // Retrying a skip would just be declined again for the same reason — the
      // server is the one saying "not now". Stop, and tell the caller no content
      // was produced so it doesn't book this as a successful recovery.
      return { success: true, skipped: result.skipped === true };
    }

    // If it's an authentication error, stop retrying and return immediately
    // The calling service should stop itself
    // Log at debug level since auth expiration is expected behavior, not an error
    if (result.isAuthError) {
      logger.debug('[AI-Resilience] Authentication error detected, stopping retries');
      return { success: false, error: result.error, isAuthError: true };
    }

    // Quota exhaustion and malformed requests fail identically every time —
    // retrying only spends more of the budget that just ran out.
    if (result.isNonRetryable) {
      logger.debug(`[AI-Resilience] Non-retryable error, stopping retries: ${result.error}`);
      return { success: false, error: result.error };
    }

    // Don't retry if it's the last attempt
    if (attempt === maxRetries) {
      return { success: false, error: result.error };
    }

    // Calculate backoff and retry
    const delayMs = calculateBackoffDelay(attempt - 1);
    logger.log(`[AI-Resilience] Retrying in ${delayMs.toFixed(0)}ms...`);
    await new Promise(resolve => setTimeout(resolve, delayMs));
  }

  return { success: false, error: 'Exhausted all retry attempts' };
}
